<!--
  @component

  The chip component

  ```html
  <oc-chip-v1 value="otto" name="brand">Otto</oc-chip-v1>
  ```

  @slot required `default` The label for the chip. Also read by screen readers.
-->
<svelte:options
  customElement={{
    tag: "oc-chip-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (customElementConstructor) => {
      return class extends customElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();
          this.host = this;
          this.attachShadow({ mode: "open", delegatesFocus: true });
          /*                                */
          this.internals = this.attachInternals();

          this.setValidity = (...args) => this.internals.setValidity(...args);
        }

        /*                                                            */
        get willValidate() {
          return this.internals.willValidate;
        }

        get validity() {
          return this.internals.validity;
        }

        get validationMessage() {
          return this.internals.validationMessage;
        }

        get form() {
          return this.internals.form;
        }

        /*                             */
        formResetCallback() {
          if (!this.removable) {
            this.checked = this.hasAttribute("checked");
          }
        }
      };
    },
    props: {
      inverted: { type: "Boolean" },
      size: { type: "String", reflect: true },
      removable: { type: "Boolean" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" },
      checked: { type: "Boolean" }, /*                                     */
      icon: { type: "String" },
      showCheckIcon: { type: "Boolean", attribute: "show-check-icon" },
      singleSelection: { type: "Boolean", attribute: "single-selection" },
    },
  }}
/>

<script lang="ts">
  import type { OcIconV1Props } from "@meta/icon";
  import { usePropertyChange } from "../../../common/utils/usePropertyChange";

  usePropertyChange();

  /**
 *
 *
 *
 *
 */
  export let inverted: boolean = false;

  /**
 *
 */
  export let size: "100" | "200" = "100";

  /**
 *
 *
 *
 */
  export let removable = false;

  /**
 *
 */
  export let disabled = false;

  /**
 *
 *
 *
 *
 */
  export let name: string | undefined = undefined;

  /**
 *
 *
 *
 *
 *
 */
  export let value: string = "on";

  /**
 *
 */
  export let checked = false;

  /**
 *
 */
  export let showCheckIcon = false;

  /**
 *
 */
  export let icon: OcIconV1Props["type"] | undefined = undefined;
  $: leftIcon = checked && showCheckIcon ? "check" : icon;

  /**
 *
 *
 *
 *
 */
  export let singleSelection = false;

  /**
 *
 */
  export let internals: ElementInternals;

  /**
 *
 */
  export let host: HTMLElement;

  $: internals?.setFormValue(checked ? value : null);

  /*                                         */
  $: role = removable ? "button" : singleSelection ? "radio" : "checkbox";

  $: ariaChecked = removable ? undefined : checked;

  const onCheckToggle = ({ code, type }: MouseEvent | KeyboardEvent) => {
    if (disabled) return;

    if (removable) {
      if (["Space", "Enter"].includes(code) || type === "click") {
        host.dispatchEvent(
          new CustomEvent<undefined>("oc-remove", {
            bubbles: true,
          }),
        );
      }
    } else if (code === "Space" || type === "click") {
      /*                                        */
      if (singleSelection && checked) return;
      checked = !checked;
    }
  };

  /*                      */
  $: if (singleSelection && name && checked && internals) {
    document.querySelectorAll(`oc-chip-v1[name="${name}"]`).forEach((chip) => {
      const isSelf = chip.value === value;
      if (!isSelf && chip.checked) {
        chip.checked = false;
      }
    });
  }
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div
  class="chip chip--size-{size}"
  class:chip--inverted={inverted}
  {role}
  aria-checked={ariaChecked}
  aria-disabled={disabled}
  tabindex="0"
  on:click={onCheckToggle}
  on:keydown={onCheckToggle}
>
  <!-- Icon Left -->
  {#if leftIcon}
    <oc-icon-v1 class="chip__icon chip__icon--{size}" type={leftIcon} />
  {/if}

  <label class="chip__label">
    <slot />
    {#if removable}
      <span class="chip__aria-remove">entfernen</span>
    {/if}
  </label>

  <!-- Removable Icon Right -->
  {#if removable}
    <oc-icon-v1 class="chip__icon chip__icon--removable chip__icon--{size}" type={"close"} />
  {/if}
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "./ChipV1.config" as config;
  @use "../../../common/scss/visually-hidden" as *;

  :host {
    @include config.oc-chip;
  }

  :host([size="100"]) {
    @include config.oc-chip-size-100;
  }

  :host([size="200"]) {
    @include config.oc-chip-size-200;
  }

  .chip {
    position: relative;
    box-sizing: border-box;

    display: flex;
    justify-content: center;
    align-items: center;

    background-color: tokens.$oc-component-chip-default-background-color;
    color: tokens.$oc-component-chip-text-color;
    cursor: pointer;
    user-select: none;

    &:focus {
      outline: 2px solid tokens.$oc-semantic-focus-outline-color;
      outline-offset: tokens.$oc-semantic-focus-outline-offset;
    }

    &:hover {
      background-color: tokens.$oc-component-chip-default-background-color-hover;
    }

    &:active {
      background-color: tokens.$oc-component-chip-default-background-color-active;
    }

    .chip__label {
      pointer-events: none;
      user-select: none;
      font-size: tokens.$oc-component-chip-100-font;
    }

    &--size-100 {
      padding: tokens.$oc-component-chip-100-padding-y tokens.$oc-component-chip-100-padding-x;
      border-radius: 20px;
      gap: tokens.$oc-component-chip-100-gap-x;
      font: tokens.$oc-component-chip-100-font;
      height: tokens.$oc-component-chip-100-height;
    }

    &--size-200 {
      padding: tokens.$oc-component-chip-200-padding-y tokens.$oc-component-chip-200-padding-x;
      border-radius: 24px;
      gap: tokens.$oc-component-chip-200-gap-x;
      font: tokens.$oc-component-chip-200-font;
      height: tokens.$oc-component-chip-200-height;
    }

    &[aria-checked="true"] {
      background-color: tokens.$oc-component-chip-selected-background-color;
      font-weight: tokens.$oc-component-chip-selected-font-weight;

      &:focus {
        background-color: tokens.$oc-component-chip-selected-background-color-hover;
      }

      &:hover {
        background-color: tokens.$oc-component-chip-selected-background-color-hover;
      }

      &:active {
        background-color: tokens.$oc-component-chip-selected-background-color-active;
      }
    }

    &[aria-disabled="true"] {
      background-color: tokens.$oc-component-chip-disabled-background-color;
      color: tokens.$oc-component-chip-disabled-icon-color;
      pointer-events: none;

      &:focus {
        background-color: tokens.$oc-component-chip-disabled-background-color;
      }

      &[aria-checked] {
        font-weight: normal;
      }

      .chip__icon {
        fill: tokens.$oc-component-chip-disabled-icon-color;
      }
    }

    &__icon {
      height: tokens.$oc-component-chip-100-icon-size;
      width: tokens.$oc-component-chip-100-icon-size;
      pointer-events: none;

      &--200 {
        align-self: auto;
        height: tokens.$oc-component-chip-200-icon-size;
        width: tokens.$oc-component-chip-200-icon-size;
      }
    }

    &--inverted {
      background-color: tokens.$oc-component-chip-inverted-background-color;

      &:hover {
        background-color: tokens.$oc-component-chip-inverted-background-color-hover;
      }

      &:active {
        background-color: tokens.$oc-component-chip-inverted-background-color-active;
      }
    }

    &__aria-remove {
      @include visually-hidden();
    }
  }
</style>
